import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './App.css';

const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || 'https://apioo2.paragraf.spb.ru',
  ENDPOINTS: {
    SCHOOLS: '/api/schools',
    COMPARISON: '/api/school',
    ZDANIYA: '/api/zdania',
    STATUS: '/api/status',
    REPORT_STATUS: '/api/report-status'
  }
};

const Modal = ({ isOpen, onClose, children }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>×</button>
          <div className="modal-body">{children}</div>
        </div>
      </div>
  );
};

function App() {
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [differences, setDifferences] = useState({});
  const [loading, setLoading] = useState({
    schools: false,
    selectedSchool: false,
    allSchools: false
  });
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [schoolsSummary, setSchoolsSummary] = useState([]);
  const [zdaniya, setZdaniya] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [signatureStatus, setSignatureStatus] = useState({});
  const [reportStatus, setReportStatus] = useState({});
  const [allSchoolsList, setAllSchoolsList] = useState([]);

  const sectionTitles = {
    '1.1': '1.1. Характеристика здания (зданий)',
    '1.2': '1.2. Сведения о помещениях (проверить)',
    '1.3': '1.3. Перевозка обучающихся, проживающих в отдаленных районах',
    '1.4': '1.4. Охват обучающихся горячим питанием',
    '1.5': '1.5. Наличие и использование площадей',
    '1.6': '1.6. Деятельность организации',
    '2.1': '2.1. Количество персональных компьютеров и информационного оборудования',
    '2.2': '2.2. Наличие специальных программных средств',
    '2.3': '2.3. Максимальная скорость доступа к сети Интернет',
    '2.4': '2.4. Информационная открытость организации',
    '2.5': '2.5. Реализация образовательных программ с применением электронного обучения',
    '2.6': '2.6. Формирование и использование библиотечного (книжного) фонда',
    '2.7': '2.7. Информационное обслуживание и другие характеристики библиотеки (проверить)',
    '3.1': '3.1. Распределение объема средств организации по источникам их получения',
    '3.2': '3.2. Расходы организации',
    '3.3': '3.3. Сведения о численности и оплате труда работников организации',
    '3.5': '3.5. Затраты на внедрение и использование цифровых технологий',
    '3.6': '3.6. Источники финансирования внутренних затрат на цифровые технологии'
  };

  // Загрузка статусов подписей
  const fetchSignatureStatus = async () => {
    try {
      const response = await axios.get(
        getApiUrl(API_CONFIG.ENDPOINTS.STATUS),
        { timeout: 5000 }
      );
      if (response.data?.success) {
        const statusMap = {};
        response.data.data.forEach(status => {
          statusMap[status.school_id] = {
            signature_data: status.signature_data,
            error_message: status.error_message,
            form: status.form,
            version: status.version
          };
        });
        setSignatureStatus(statusMap);
        console.log('Статусы подписей загружены:', statusMap);
      }
    } catch (err) {
      console.error('Ошибка загрузки статусов подписей:', err);
    }
  };

  // Загрузка статуса сдачи отчетов
  const fetchReportStatus = async () => {
    try {
      const response = await axios.get(
        getApiUrl(API_CONFIG.ENDPOINTS.REPORT_STATUS),
        { timeout: 5000 }
      );
      if (response.data?.success) {
        const statusMap = {};
        const schoolsList = [];
        response.data.data.forEach(school => {
          statusMap[school.school_id] = {
            has_data: school.has_data,
            signature_data: school.signature_data,
            error_message: school.error_message,
            report_status: school.report_status
          };
          schoolsList.push({
            id: school.school_id,
            name: school.school_name
          });
        });
        setReportStatus(statusMap);
        setAllSchoolsList(schoolsList);
        console.log('Статус сдачи отчетов загружен:', statusMap);
      }
    } catch (err) {
      console.error('Ошибка загрузки статуса сдачи отчетов:', err);
    }
  };

  useEffect(() => {
    const fetchLastSync = async () => {
      try {
        const response = await axios.get(
            getApiUrl('/api/last-sync'),
            { timeout: 5000 }
        );
        if (response.data?.success) {
          setLastSync(response.data.lastSync);
        }
      } catch (err) {
        console.error('Ошибка загрузки времени синхронизации', err);
      }
    };

    fetchLastSync();
    fetchSignatureStatus();
    fetchReportStatus();
    const interval = setInterval(fetchLastSync, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchZdaniya = async () => {
      try {
        const response = await axios.get(
            getApiUrl(API_CONFIG.ENDPOINTS.ZDANIYA),
            { timeout: 5000 }
        );
        if (response.data) {
          setZdaniya(response.data);
        }
      } catch (err) {
        console.error('Ошибка загрузки данных о зданиях', err);
      }
    };

    fetchZdaniya();
  }, []);

  const formatLastSync = (dateString) => {
    if (!dateString) return 'данные не загружены';

    const date = new Date(dateString);
    date.setHours(date.getHours() - 3);

    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getApiUrl = (endpoint, pathParam = '') => {
    return `${API_CONFIG.BASE_URL}${endpoint}${pathParam}`;
  };

  const algorithmDescription = `
<strong>Обновление данных с федерального сервера происходит автоматически каждый час</strong>

<h3>Основные алгоритмы проверки данных отчёта ОО-2:</h3>

<strong>1.1 Характеристика здания (зданий)</strong>
<ul>
  <li>🔴 Количество зданий: сравнивается с данными из АИСУ "Параграф"</li>
  <li>🔴 Даты ввода и капремонта: проверяется соответствие годов с "Параграфом"</li>
  <li>🔴 Проектная мощность: сверяется с данными из "Параграфа"</li>
  <li>🔴 Строка 1: проверяются соответствия между столбцами 2024 и 2025 годов</li>
  <li>🔴 Нулевые значения: проверяются столбцы, которые должны быть 0 в 2025</li>
  <li>🔴 Количество зданий в строке 1: сравнивается с фактическим количеством</li>
  <li>🟡 Количество огнетушителей: проверяется соответствие между годами</li>
</ul>

<strong>1.4 Охват обучающихся горячим питанием</strong>
<ul>
  <li>🔴 Строка 1, столбец 3: сравнивается с разделом 3.4</li>
  <li>🔴 Строка 7, столбец 3: проверяется, что значение > 0 (льготники)</li>
  <li>🟡 Строки 5 и 6: проверяются изменения между годами</li>
</ul>

<strong>2.1 Количество персональных компьютеров</strong>
<ul>
  <li>🔴 Формула: компьютеры в 2025 = компьютеры в 2024 + новые компьютеры</li>
  <li>🔵 Различия с 2024 годом: выводятся для информации</li>
</ul>

<strong>2.3 Скорость доступа к сети Интернет</strong>
<ul>
  <li>🔴 Строки 1 и 2, столбец 3: проверка скорости >6 (в ЕМТС >50 Мбит/с)</li>
</ul>

<strong>2.6 Формирование библиотечного фонда</strong>
<ul>
  <li>🔴 Формула: книги в 2025 = книги в 2024 + новые - списанные</li>
</ul>

<strong>3.1 Распределение объема средств</strong>
<ul>
  <li>🔴 Строка 5, столбец 3: должно быть 0</li>
  <li>🔴 Строка 10, столбец 3: сравнивается с остатком на конец 2024</li>
  <li>🔴 Строка 12, столбец 3: должно быть 1</li>
</ul>

<strong>3.2 Расходы организации</strong>
<ul>
  <li>🔴 Строка 20, столбец 3: должно быть 1</li>
</ul>

<strong>3.3 Сведения о численности</strong>
<ul>
  <li>🔴 Строка 11, столбец 3: должно быть 1</li>
</ul>

<strong>3.5 Затраты на цифровые технологии</strong>
<ul>
  <li>🟡 Строка 1, столбец 3: проверка > 0 </li>
</ul>

<strong>3.6 Источники финансирования цифровых технологий</strong>
<ul>
  <li>🟡 Строка 1, столбец 3: проверка (0 < значение ≤ 4000 тыс. руб.)</li>
</ul>

<h3>Общие особенности проверки:</h3>
<ul>
  <li>🔴 - критическая ошибка (требует исправления)</li>
  <li>🟡 - предупреждение (рекомендуется исправить)</li>
  <li>🔵 - информационное сообщение</li>
</ul>
`;

  const exportToExcel = () => {
    if (!selectedSchool) {
      setError('Не выбрана школа для экспорта');
      return;
    }

    const workbook = XLSX.utils.book_new();
    const allData = [];

    allData.push(['Раздел', 'Название раздела', 'Строка', 'Колонка', '2024', '2025', 'Разница', 'Ожидаемое', 'Комментарий', 'Критичность']);

    Object.entries(differences).forEach(([section, data]) => {
      if (data && data.length > 0) {
        data.forEach(item => {
          let severity = '';
          if (item.difference === 'Несоответствие' || item.difference === 'Несоответствие формуле') {
            severity = '🔴 Критическая';
          } else if (item.difference === 'Предупреждение' || item.difference === 'Проверить') {
            severity = '🟡 Предупреждение';
          } else {
            severity = '🔵 Информация';
          }

          allData.push([
            section,
            sectionTitles[section] || '',
            item.row,
            item.col,
            item.value_2023,
            item.value_2024,
            item.difference,
            item.expected || '',
            item.message || '',
            severity
          ]);
        });
      }
    });

    if (allData.length === 1) {
      allData.push(['', '', '', '', '', '', 'Недочетов не найдено', '', '', '']);
    }

    const worksheet = XLSX.utils.aoa_to_sheet(allData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Отчет');
    XLSX.writeFile(workbook, `Отчет_Школа_${selectedSchool}.xlsx`);
  };

  const exportSummaryToExcel = () => {
    const workbook = XLSX.utils.book_new();
    
    // Создаем данные для сводного отчета
    const summaryData = [];
    
    // Заголовки
    summaryData.push([
      'Школа',
      'Статус отчета',
      'ЭЦП',
      ...Object.values(sectionTitles),
      'Всего замечаний'
    ]);
    
    // Создаем мапу для быстрого доступа к данным
    const summaryMap = {};
    schoolsSummary.forEach(school => {
      summaryMap[school.id] = school;
    });
    
    // Заполняем данные по всем школам
    allSchoolsList.forEach(school => {
      const schoolSummary = summaryMap[school.id];
      const reportInfo = reportStatus[school.id];
      const hasData = reportInfo?.has_data || false;
      const signatureInfo = signatureStatus[school.id];
      const hasSignature = signatureInfo?.signature_data === 'yes';
      
      const row = [
        school.name,
        hasData ? 'Предоставлен' : 'Не предоставлен',
        hasSignature ? 'Есть' : (signatureInfo?.signature_data === 'no' ? 'Нет' : 'Не определен')
      ];
      
      // Добавляем количество замечаний по каждому разделу
      Object.keys(sectionTitles).forEach(section => {
        const count = schoolSummary?.differences[section]?.length || 0;
        row.push(count);
      });
      
      // Добавляем общее количество замечаний
      row.push(schoolSummary?.issuesCount || 0);
      
      summaryData.push(row);
    });
    
    const worksheet = XLSX.utils.aoa_to_sheet(summaryData);
    
    // Настройка ширины колонок
    const colWidths = [
      { wch: 15 }, // Школа
      { wch: 15 }, // Статус отчета
      { wch: 10 }, // ЭЦП
      ...Object.keys(sectionTitles).map(() => ({ wch: 8 })), // Разделы
      { wch: 12 } // Всего замечаний
    ];
    worksheet['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Сводный отчет');
    
    // Создаем отдельный лист с детальными замечаниями для всех школ
    const detailsData = [];
    detailsData.push(['Школа', 'Раздел', 'Название раздела', 'Строка', 'Колонка', '2024', '2025', 'Разница', 'Ожидаемое', 'Комментарий', 'Критичность']);
    
    schoolsSummary.forEach(school => {
      Object.entries(school.differences).forEach(([section, data]) => {
        if (data && data.length > 0) {
          data.forEach(item => {
            let severity = '';
            if (item.difference === 'Несоответствие' || item.difference === 'Несоответствие формуле') {
              severity = '🔴 Критическая';
            } else if (item.difference === 'Предупреждение' || item.difference === 'Проверить') {
              severity = '🟡 Предупреждение';
            } else {
              severity = '🔵 Информация';
            }
            
            detailsData.push([
              school.name,
              section,
              sectionTitles[section] || '',
              item.row,
              item.col,
              item.value_2023,
              item.value_2024,
              item.difference,
              item.expected || '',
              item.message || '',
              severity
            ]);
          });
        }
      });
    });
    
    if (detailsData.length === 1) {
      detailsData.push(['', '', '', '', '', '', '', 'Недочетов не найдено', '', '', '']);
    }
    
    const detailsWorksheet = XLSX.utils.aoa_to_sheet(detailsData);
    XLSX.utils.book_append_sheet(workbook, detailsWorksheet, 'Детальные замечания');
    
    // Создаем лист со статистикой по школам
    const statsData = [];
    statsData.push(['Показатель', 'Значение']);
    statsData.push(['Всего школ', allSchoolsList.length]);
    statsData.push(['Школ, предоставивших отчет', allSchoolsList.filter(school => reportStatus[school.id]?.has_data).length]);
    statsData.push(['Школ, не предоставивших отчет', allSchoolsList.filter(school => !reportStatus[school.id]?.has_data).length]);
    statsData.push(['Школ с подписью', Object.values(signatureStatus).filter(s => s?.signature_data === 'yes').length]);
    statsData.push(['Школ без подписи', Object.values(signatureStatus).filter(s => s?.signature_data === 'no').length]);
    statsData.push(['Всего замечаний', schoolsSummary.reduce((sum, school) => sum + school.issuesCount, 0)]);
    statsData.push(['Среднее количество замечаний на школу', (schoolsSummary.reduce((sum, school) => sum + school.issuesCount, 0) / (schoolsSummary.length || 1)).toFixed(2)]);
    
    // Добавляем статистику по разделам
    statsData.push(['', '']);
    statsData.push(['Статистика по разделам', '']);
    Object.keys(sectionTitles).forEach(section => {
      const totalIssues = schoolsSummary.reduce((sum, school) => sum + (school.differences[section]?.length || 0), 0);
      statsData.push([sectionTitles[section], totalIssues]);
    });
    
    const statsWorksheet = XLSX.utils.aoa_to_sheet(statsData);
    XLSX.utils.book_append_sheet(workbook, statsWorksheet, 'Статистика');
    
    // Сохраняем файл
    const date = new Date();
    const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    XLSX.writeFile(workbook, `Сводный_отчет_ОО-2_${dateStr}.xlsx`);
  };

  const calculateDifference = (value1, value2) => {
    if (value1 == null || value2 == null) return '—';

    const num1 = parseFloat(value1);
    const num2 = parseFloat(value2);

    if (!isNaN(num1) && !isNaN(num2)) {
      const diff = num2 - num1;
      return diff === 0 ? '0' : diff.toFixed(2);
    }

    return value1 === value2 ? 'Одинаково' : 'Разные';
  };

  const processDifferences = (allData, zdaniyaForSchool = []) => {
    const newDifferences = {
      '1.1': [], '1.2': [], '1.3': [], '1.4': [], '1.5': [], '1.6': [],
      '2.1': [], '2.2': [], '2.3': [], '2.4': [], '2.5': [], '2.6': [], '2.7': [],
      '3.1': [], '3.2': [], '3.3': [], '3.5': [], '3.6': []
    };

    // Проверка количества зданий
    if (zdaniyaForSchool.length > 0) {
      const zdaniyaCount = zdaniyaForSchool.length;
      const buildingRows = new Set(
          allData
              .filter(item => item.section === '1.1' && item.row && item.row.includes('.'))
              .map(item => item.row)
      );
      const buildingsInReport = buildingRows.size;

      if (buildingsInReport !== zdaniyaCount) {
        newDifferences['1.1'].push({
          section: '1.1',
          row: 'Кол-во зданий',
          col: '—',
          value_2023: '—',
          value_2024: buildingsInReport.toString(),
          difference: 'Несоответствие',
          expected: zdaniyaCount.toString(),
          message: `🔴 Количество зданий (${buildingsInReport}) ≠ данным АИСУ "Параграф" (${zdaniyaCount}). В подразделе информация представляется по всем зданиям в которых непосредственно осуществляется образовательная деятельность`,
          severity: '🔴'
        });
      }
    }

    if (zdaniyaForSchool.length > 0) {
      const buildingData = allData
          .filter(item => item.section === '1.1' && item.row && item.row.includes('.'))
          .reduce((acc, item) => {
            if (!acc[item.row]) acc[item.row] = {};
            acc[item.row][item.col] = item;
            return acc;
          }, {});

      zdaniyaForSchool.forEach((zdanie, index) => {
        const buildingRow = `${index + 1}.${index + 1}`;

        if (buildingData[buildingRow]) {
          const building = buildingData[buildingRow];

          // Проверка столбца 4 (дата ввода)
          const col4 = building['4'];
          if (col4) {
            const reportedYear = extractYear(col4.value_2024);
            const expectedYear = extractYear(zdanie.date_start);

            if (reportedYear && expectedYear && reportedYear !== expectedYear) {
              newDifferences['1.1'].push({
                section: '1.1',
                row: buildingRow,
                col: '4',
                value_2023: col4.value_2023 || '—',
                value_2024: col4.value_2024,
                difference: 'Несоответствие',
                expected: expectedYear,
                message: `🔴 Год ввода здания (ОУ ${zdanie.OU}) не соответствует данным АИСУ "Параграф" (${expectedYear})`,
                severity: '🔴'
              });
            }
          }

          // Проверка столбца 5 (дата капремонта)
          const col5 = building['5'];
          if (col5) {
            const reportedYear = extractYear(col5.value_2024);
            const zdanieYear = zdanie.date_capremont ? extractYear(zdanie.date_capremont) : null;
            const expectedYear = zdanieYear || '-';

            if (reportedYear && reportedYear !== expectedYear) {
              newDifferences['1.1'].push({
                section: '1.1',
                row: buildingRow,
                col: '5',
                value_2023: col5.value_2023 || '—',
                value_2024: col5.value_2024,
                difference: 'Несоответствие',
                expected: expectedYear,
                message: expectedYear === '0'
                    ? `🔴 В АИСУ "Параграф" не указана дата капремонта для здания`
                    : `🔴 Год капремонта здания не соответствует данным АИСУ "Параграф" (${expectedYear})`,
                severity: '🔴'
              });
            }
          }

          // Проверка столбца 19 (проектная мощность)
          const col19 = building['19'];
          if (col19) {
            const reportedCapacity = parseNumber(col19.value_2024);
            const expectedCapacity = parseNumber(zdanie.project);

            if (reportedCapacity !== null && expectedCapacity !== null && reportedCapacity !== expectedCapacity) {
              newDifferences['1.1'].push({
                section: '1.1',
                row: buildingRow,
                col: '19',
                value_2023: col19.value_2023 || '—',
                value_2024: col19.value_2024,
                difference: 'Несоответствие',
                expected: expectedCapacity.toString(),
                message: `🔴 Проектная мощность здания (ОУ ${zdanie.OU}) не соответствует данным АИСУ "Параграф" (${expectedCapacity})`,
                severity: '🔴'
              });
            }
          }
        }
      });
    }

    function extractYear(dateString) {
      if (!dateString || dateString === '0') return null;
      const yearMatch = dateString.match(/(\d{4})/);
      if (yearMatch) return yearMatch[1];
      try {
        return new Date(dateString).getFullYear().toString();
      } catch {
        return null;
      }
    }

    function parseNumber(value) {
      if (value === null || value === undefined || value === '') return null;
      const num = parseFloat(value.toString().replace(',', '.'));
      return isNaN(num) ? null : num;
    }

    // Проверка строки 1 раздела 1.1
    const section1_1 = allData.filter(item => item.section === '1.1');
    const row1_2023 = {};
    const row1_2024 = {};

    section1_1.forEach(item => {
      if (item.row === '1') {
        if (item.value_2023 !== undefined) row1_2023[item.col] = item.value_2023;
        if (item.value_2024 !== undefined) row1_2024[item.col] = item.value_2024;
      }
    });

    const row1Checks = [
      { col2024: '1', col2023: '1', message: '🔴 столбец 1 2025 должен быть равен столбцу 1 2024' },
      { col2024: '2', col2023: '2', message: '🔴 столбец 2 2025 должен быть равен столбцу 2 2024' },
      { col2024: '3', col2023: '3', message: '🔴 столбец 3 2025 должен быть равен столбцу 3 2024' },
      { col2024: '4', col2023: '4', message: '🔴 столбец 4 2025 должен быть равен столбцу 4 2024' },
      { col2024: '5', col2023: '5', message: '🔴 столбец 5 2025 должен быть равен столбцу 5 2024' },
      { col2024: '6', col2023: '6', message: '🔴 столбец 6 2025 должен быть равен столбцу 6 2024' },
      { col2024: '7', col2023: '7', message: '🔴 столбец 7 2025 должен быть равен столбцу 7 2024' },
      { col2024: '8', col2023: '8', message: '🔴 столбец 8 2025 должен быть равен столбцу 8 2024' },
      { col2024: '9', col2023: '9', message: '🔴 столбец 9 2025 должен быть равен столбцу 9 2024' },
      { col2024: '10', col2023: '10', message: '🔴 столбец 10 2025 должен быть равен столбцу 10 2024' },
      { col2024: '11', col2023: '11', message: '🔴 столбец 11 2025 должен быть равен столбцу 11 2024' },
      { col2024: '12', col2023: '12', message: '🔴 столбец 12 2025 должен быть равен столбцу 12 2024' },
      { col2024: '13', col2023: '13', message: '🔴 столбец 13 2025 должен быть равен столбцу 13 2024' },
      { col2024: '14', col2023: '14', message: '🔴 столбец 14 2025 должен быть равен столбцу 14 2024' },
      { col2024: '15', col2023: '15', message: '🔴 столбец 15 2025 должен быть равен столбцу 15 2024' },
      { col2024: '16', col2023: '16', message: '🔴 столбец 16 2025 должен быть равен столбцу 16 2024' },
      { col2024: '17', col2023: '17', message: '🔴 столбец 17 2025 должен быть равен столбцу 17 2024' },
      { col2024: '18', col2023: '18', message: '🔴 столбец 18 2025 должен быть равен столбцу 18 2024' },
      { col2024: '19', col2023: '19', message: '🔴 столбец 19 2025 должен быть равен столбцу 19 2024' },
      { col2024: '20', col2023: '20', message: '🔴 столбец 20 2025 должен быть равен столбцу 20 2024' },
      { col2024: '21', col2023: '21', message: '🔴 столбец 21 2025 должен быть равен столбцу 21 2024' },
      { col2024: '22', col2023: '22', message: '🔴 столбец 22 2025 должен быть равен столбцу 22 2024' },
      { col2024: '23', col2023: '23', message: '🔴 столбец 23 2025 должен быть равен столбцу 23 2024' },
      { col2024: '24', col2023: '24', message: '🔴 столбец 24 2025 должен быть равен столбцу 24 2024' },
      { col2024: '25', col2023: '25', message: '🔴 столбец 25 2025 должен быть равен столбцу 25 2024' },
      { col2024: '26', col2023: '26', message: '🔴 столбец 26 2025 должен быть равен столбцу 26 2024' },
      { col2024: '27', col2023: '27', message: '🔴 столбец 27 2025 должен быть равен столбцу 27 2024' },
      { col2024: '28', col2023: '28', message: '🔴 столбец 28 2025 должен быть равен столбцу 28 2024' },
      { col2024: '29', col2023: '29', message: '🔴 столбец 29 2025 должен быть равен столбцу 29 2024' },
      { col2024: '30', col2023: '30', message: '🔴 столбец 30 2025 должен быть равен столбцу 30 2024' }
    ];

    row1Checks.forEach(check => {
      if (row1_2024[check.col2024] !== undefined && row1_2023[check.col2023] !== undefined &&
          row1_2024[check.col2024] !== row1_2023[check.col2023]) {
        newDifferences['1.1'].push({
          section: '1.1',
          row: '1',
          col: check.col2024,
          value_2023: row1_2023[check.col2023] || '—',
          value_2024: row1_2024[check.col2024],
          difference: 'Несоответствие',
          expected: row1_2023[check.col2023],
          message: check.message,
          severity: '🔴'
        });
      }
    });

    const zeroCols2024 = ['10', '11', '20', '22', '23', '24', '25', '26', '27'];
    zeroCols2024.forEach(col => {
      if (row1_2024[col] !== undefined && row1_2024[col] !== '0') {
        newDifferences['1.1'].push({
          section: '1.1',
          row: '1',
          col: col,
          value_2023: row1_2023[col] || '—',
          value_2024: row1_2024[col],
          difference: 'Несоответствие',
          expected: '0',
          message: `🔴 столбец ${col} 2025 должен быть равен 0`,
          severity: '🔴'
        });
      }
    });

    const buildingRows = new Set(
        allData
            .filter(item => item.section === '1.1' && item.row && item.row.includes('.'))
            .map(item => item.row)
    );
    const buildingsInReport = buildingRows.size;

    const col18_2024 = allData.find(item =>
        item.section === '1.1' &&
        item.row === '1' &&
        item.col === '18'
    )?.value_2024;

    if (col18_2024 !== undefined) {
      const reportedBuildingCount = parseInt(col18_2024);

      if (!isNaN(reportedBuildingCount)) {
        if (reportedBuildingCount !== buildingsInReport) {
          newDifferences['1.1'].push({
            section: '1.1',
            row: '1',
            col: '18',
            value_2023: allData.find(item =>
                item.section === '1.1' &&
                item.row === '1' &&
                item.col === '18'
            )?.value_2023 || '—',
            value_2024: col18_2024,
            difference: 'Несоответствие',
            expected: buildingsInReport.toString(),
            message: `🔴 Столбцев 18 (${reportedBuildingCount}) не соответствует фактическому количеству зданий (${buildingsInReport})`,
            severity: '🔴'
          });
        }
      } else {
        newDifferences['1.1'].push({
          section: '1.1',
          row: '1',
          col: '18',
          value_2023: allData.find(item =>
              item.section === '1.1' &&
              item.row === '1' &&
              item.col === '18'
          )?.value_2023 || '—',
          value_2024: col18_2024,
          difference: 'Ошибка',
          expected: buildingsInReport.toString(),
          message: '🔴 Некорректное значение в столбце 18 (должно быть числом)',
          severity: '🔴'
        });
      }
    }

    // Проверка строки 3 (столбец 3)
    const row3_2023 = section1_1.find(item => item.row === '3' && item.col === '3')?.value_2023;
    const row3_2024 = section1_1.find(item => item.row === '3' && item.col === '3')?.value_2024;

    if (row3_2023 !== undefined && row3_2024 !== undefined && row3_2024 !== row3_2023) {
      newDifferences['1.1'].push({
        section: '1.1',
        row: 'строка 3 столбец 3',
        col: '3',
        value_2023: row3_2023,
        value_2024: row3_2024,
        difference: 'Несоответствие',
        expected: row3_2023,
        message: '🟡 Кол-во огнетушителей 2025 не равен строке 3 столбцу 3 2024 (была закупка или списание?)',
        severity: '🟡'
      });
    }

    // Проверка раздела 1.4
    const row1Col3_2024_1_4 = allData.find(item =>
        item.section === '1.4' && item.row === '1' && item.col === '3'
    )?.value_2024;

    const row2Col3_2024_3_4 = allData.find(item =>
        item.section === '3.4' && item.row === '2' && item.col === '3'
    )?.value_2024;

    if (row1Col3_2024_1_4 !== row2Col3_2024_3_4) {
      newDifferences['1.4'].push({
        section: '1.4',
        row: '1',
        col: '3',
        value_2023: allData.find(item =>
            item.section === '1.4' && item.row === '1' && item.col === '3'
        )?.value_2023 || '—',
        value_2024: row1Col3_2024_1_4,
        difference: 'Несоответствие',
        expected: row2Col3_2024_3_4 || '—',
        actual: row1Col3_2024_1_4 || '—',
        message: '🔴 Значение должно быть равно значению из раздела 3.4 (строка 2 графа 3)',
        severity: '🔴'
      });
    }

    const row7Col3_2024_1_4 = allData.find(item =>
        item.section === '1.4' && item.row === '7' && item.col === '3'
    )?.value_2024;

    if (row7Col3_2024_1_4 && parseFloat(row7Col3_2024_1_4) <= 0) {
      newDifferences['1.4'].push({
        section: '1.4',
        row: '7',
        col: '3',
        value_2023: allData.find(item =>
            item.section === '1.4' && item.row === '7' && item.col === '3'
        )?.value_2023 || '—',
        value_2024: row7Col3_2024_1_4,
        difference: 'Несоответствие',
        expected: '> 0',
        actual: row7Col3_2024_1_4 || '0',
        message: '🔴 Численность обучающихся 1-4 класса, имеющих льготы по питанию, кроме обеспеченных бесплатным горячим питанием за счет субсидии из федерального бюджета, предположительно больше 0 (льготники, получающие обеды)',
        severity: '🔴'
      });
    }

    // Остальные проверки для раздела 1.4
    ['5', '6'].forEach(row => {
      const item = allData.find(i =>
          i.section === '1.4' && i.row === row && i.col === '3'
      );
      if (item) {
        const diff = calculateDifference(item.value_2023, item.value_2024);
        if (diff !== '0' && diff !== 'Одинаково') {
          newDifferences['1.4'].push({
            ...item,
            difference: diff,
            message: '🟡 Изменение между годами',
            severity: '🟡'
          });
        }
      }
    });

    // Стандартные проверки для других разделов
    const standardSections = ['1.2', '1.3', '1.5', '1.6', '2.2', '2.3', '2.4', '2.5'];
    standardSections.forEach(section => {
      newDifferences[section] = allData
          .filter(item => item.section === section && item.difference !== 'Одинаково')
          .map(item => ({
            ...item,
            difference: calculateDifference(item.value_2023, item.value_2024),
            message: '🟡 Различие с предыдущим годом (информация)',
            severity: '🟡'
          }))
          .filter(item => item.difference !== '0');
    });

    newDifferences['2.1'] = allData
        .filter(item => item.section === '2.1' && item.difference !== 'Одинаково')
        .filter(item => item.row !== '16')
        .map(item => ({
          ...item,
          difference: calculateDifference(item.value_2023, item.value_2024),
          message: '🔵 Различие с предыдущим годом (информация)',
          severity: '🔵'
        }))
        .filter(item => item.difference !== '0');

    // Проверка раздела 2.3
    newDifferences['2.3'] = [];

    ['1', '2'].forEach(row => {
      const item = allData.find(i =>
          i.section === '2.3' && i.row === row && i.col === '3'
      );
      if (item) {
        const value = parseFloat(item.value_2024 || 0);
        if (value <= 6) {
          newDifferences['2.3'].push({
            section: '2.3',
            row,
            col: '3',
            value_2023: '—',
            value_2024: item.value_2024 || '0',
            difference: 'Несоответствие',
            expected: '> 6 Мбит/с',
            actual: `${item.value_2024 || '0'} Мбит/с`,
            message: '🔴 Минимальная скорость интернета > 6 (в ЕМТС - более 50 Мбит/с)',
            severity: '🔴'
          });
        }
      }
    });

    newDifferences['2.7'] = allData
        .filter(item => item.section === '2.7' && item.difference !== 'Одинаково')
        .filter(item => !['4', '5'].includes(item.row))
        .map(item => ({
          ...item,
          difference: calculateDifference(item.value_2023, item.value_2024),
          message: '🔵 Различие с предыдущим годом (информация)',
          severity: '🔵'
        }))
        .filter(item => item.difference !== '0');

    // Проверка раздела 2.6
    const row1Col5_2023 = allData.find(item =>
        item.section === '2.6' && item.row === '1' && item.col === '5'
    )?.value_2023;

    const row1Col5_2024 = allData.find(item =>
        item.section === '2.6' && item.row === '1' && item.col === '5'
    )?.value_2024;

    const row1Col3_2024 = allData.find(item =>
        item.section === '2.6' && item.row === '1' && item.col === '3'
    )?.value_2024;

    const row1Col4_2024 = allData.find(item =>
        item.section === '2.6' && item.row === '1' && item.col === '4'
    )?.value_2024;

    const calculatedValue =
        parseFloat(row1Col5_2023 || 0) +
        parseFloat(row1Col3_2024 || 0) -
        parseFloat(row1Col4_2024 || 0);

    const expectedValue = parseFloat(row1Col5_2024 || 0);

    if (Math.abs(calculatedValue - expectedValue) > 0.01) {
      newDifferences['2.6'] = [{
        section: '2.6',
        row: '1',
        col: '5',
        value_2023: row1Col5_2023,
        value_2024: row1Col5_2024,
        difference: 'Несоответствие формуле',
        expected: calculatedValue.toFixed(2),
        actual: expectedValue.toFixed(2),
        details: {
          row1Col5_2023: row1Col5_2023,
          row1Col3_2024: row1Col3_2024,
          row1Col4_2024: row1Col4_2024
        },
        message: '🔴 Несоответствие формуле расчета библиотечного фонда',
        severity: '🔴'
      }];
    } else {
      newDifferences['2.6'] = [];
    }

    // Проверка раздела 2.1 (формула для строки 1 колонка 3)
    const row1Col3_2023_2_1 = allData.find(item =>
        item.section === '2.1' && item.row === '1' && item.col === '3'
    )?.value_2023;

    const row1Col3_2024_2_1 = allData.find(item =>
        item.section === '2.1' && item.row === '1' && item.col === '3'
    )?.value_2024;

    const row7Col3_2023_2_1 = allData.find(item =>
        item.section === '2.1' && item.row === '7' && item.col === '3'
    )?.value_2024;

    const calculatedValue2_1 =
        parseFloat(row1Col3_2023_2_1 || 0) +
        parseFloat(row7Col3_2023_2_1 || 0);

    const expectedValue2_1 = parseFloat(row1Col3_2024_2_1 || 0);

    if (Math.abs(calculatedValue2_1 - expectedValue2_1) > 0.01) {
      newDifferences['2.1'].push({
        section: '2.1',
        row: '1',
        col: '3',
        value_2023: row1Col3_2023_2_1,
        value_2024: row1Col3_2024_2_1,
        difference: 'Несоответствие формуле',
        expected: calculatedValue2_1.toFixed(2),
        actual: expectedValue2_1.toFixed(2),
        details: {
          row1Col3_2023: row1Col3_2023_2_1,
          row7Col3_2023: row7Col3_2023_2_1
        },
        message: '🔴 Несоответствие формуле расчета количества компьютеров. Было списание?',
        severity: '🔴'
      });
    }

    // Проверка раздела 3.1
    const row5Col3_2024_3_1 = allData.find(item =>
        item.section === '3.1' && item.row === '5' && item.col === '3'
    )?.value_2024;

    const row10Col3_2024_3_1 = allData.find(item =>
        item.section === '3.1' && item.row === '10' && item.col === '3'
    )?.value_2024;

    const row11Col3_2023_3_1 = allData.find(item =>
        item.section === '3.1' && item.row === '11' && item.col === '3'
    )?.value_2023;

    newDifferences['3.1'] = [];

    if (row5Col3_2024_3_1 !== '0') {
      newDifferences['3.1'].push({
        section: '3.1',
        row: '5',
        col: '3',
        value_2023: '—',
        value_2024: row5Col3_2024_3_1,
        difference: 'Несоответствие',
        expected: '0',
        actual: row5Col3_2024_3_1 || 'Не указано',
        message: '🔴 Строка 5 графа 3 должна быть равна 0',
        severity: '🔴'
      });
    }

    if (row10Col3_2024_3_1 !== row11Col3_2023_3_1) {
      newDifferences['3.1'].push({
        section: '3.1',
        row: '10',
        col: '3',
        value_2023: row11Col3_2023_3_1,
        value_2024: row10Col3_2024_3_1,
        difference: 'Несоответствие',
        expected: row11Col3_2023_3_1,
        actual: row10Col3_2024_3_1,
        message: '🔴 Остаток на конец 2024 года не соответствует остатку на начало 2025 года',
        severity: '🔴'
      });
    }

    const row12Col3_2024_3_1 = allData.find(item =>
        item.section === '3.1' && item.row === '12' && item.col === '3'
    )?.value_2024;

    if (row12Col3_2024_3_1 !== '1') {
      newDifferences['3.1'].push({
        section: '3.1',
        row: '12',
        col: '3',
        value_2023: '—',
        value_2024: row12Col3_2024_3_1 || 'Не указано',
        difference: 'Несоответствие',
        expected: '1',
        message: '🔴 Значение должно быть равно 1',
        severity: '🔴'
      });
    }

    // Проверка раздела 3.2
    const row20Col3_2024_3_2 = allData.find(item =>
        item.section === '3.2' && item.row === '20' && item.col === '3'
    )?.value_2024;

    newDifferences['3.2'] = [];

    if (row20Col3_2024_3_2 !== '1') {
      newDifferences['3.2'].push({
        section: '3.2',
        row: '20',
        col: '3',
        value_2023: '—',
        value_2024: row20Col3_2024_3_2 || 'Не указано',
        difference: 'Несоответствие',
        expected: '1',
        message: '🔴 должно быть значение 1',
        severity: '🔴'
      });
    }

    // Проверка раздела 3.3
    const row11Col3_2024_3_3 = allData.find(item =>
        item.section === '3.3' && item.row === '11' && item.col === '3'
    )?.value_2024;

    newDifferences['3.3'] = [];

    if (row11Col3_2024_3_3 !== '1') {
      newDifferences['3.3'].push({
        section: '3.3',
        row: '11',
        col: '3',
        value_2023: '—',
        value_2024: row11Col3_2024_3_3 || 'Не указано',
        difference: 'Несоответствие',
        expected: '1',
        message: '🔴 должно быть значение 1',
        severity: '🔴'
      });
    }

    // Проверка раздела 3.5
    const row1Col3_2024_3_5 = allData.find(item =>
        item.section === '3.5' && item.row === '1' && item.col === '3'
    )?.value_2024;
    const numValue3_5 = parseFloat(row1Col3_2024_3_5 || 0);

    newDifferences['3.5'] = [];

    if (numValue3_5 == 0) {
      newDifferences['3.5'].push({
        section: '3.5',
        row: '1',
        col: '3',
        value_2023: '—',
        value_2024: row1Col3_2024_3_5,
        difference: 'Предупреждение',
        message: '🟡 Значение должно быть > 0',
        severity: '🟡'
      });
    }

    // Проверка раздела 3.6
    const row1Col3_2024_3_6 = allData.find(item =>
        item.section === '3.6' && item.row === '1' && item.col === '3'
    )?.value_2024;
    const numValue3_6 = parseFloat(row1Col3_2024_3_6 || 0);

    newDifferences['3.6'] = [];

    if (numValue3_6 === 0 || numValue3_6 > 4000) {
      newDifferences['3.6'].push({
        section: '3.6',
        row: '1',
        col: '3',
        value_2023: '—',
        value_2024: row1Col3_2024_3_6,
        difference: 'Проверить',
        expected: '',
        actual: row1Col3_2024_3_6 || '0',
        message: numValue3_6 === 0
            ? '🟡 Проверить: значение равно 0'
            : '🟡 Проверить: значение превышает 4000 тыс. руб.',
        severity: '🟡'
      });
    }

    return newDifferences;
  };

  const fetchSchoolDifferences = async (schoolId) => {
    try {
      const [response, zdaniyaResponse] = await Promise.all([
        axios.get(
            getApiUrl(API_CONFIG.ENDPOINTS.COMPARISON, `/${schoolId}/comparison`),
            { timeout: 10000 }
        ),
        axios.get(getApiUrl(API_CONFIG.ENDPOINTS.ZDANIYA), { timeout: 5000 })
      ]);

      if (!response.data?.success || !Array.isArray(response.data.data)) {
        throw new Error(response.data?.error || 'Неверный формат данных');
      }

      const zdaniyaForSchool = zdaniyaResponse.data.data?.filter(z => z.OU === schoolId) || [];
      return processDifferences(response.data.data, zdaniyaForSchool);
    } catch (err) {
      console.error(`Ошибка загрузки данных для школы ${schoolId}:`, err);
      return null;
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(prev => ({ ...prev, schools: true, allSchools: true }));
        setError(null);

        const [schoolsResponse, zdaniyaResponse] = await Promise.all([
          axios.get(getApiUrl(API_CONFIG.ENDPOINTS.SCHOOLS)),
          axios.get(getApiUrl(API_CONFIG.ENDPOINTS.ZDANIYA), { timeout: 5000 })
        ]);

        const formattedSchools = Array.isArray(schoolsResponse.data)
            ? schoolsResponse.data.map(school => ({
              id: String(school.id),
              name: school.name || `Школа ${school.id}`
            }))
            : [];
        setSchools(formattedSchools);

        setZdaniya(zdaniyaResponse.data);

        const summary = [];
        for (const school of formattedSchools) {
          try {
            const response = await axios.get(
                getApiUrl(API_CONFIG.ENDPOINTS.COMPARISON, `/${school.id}/comparison`),
                { timeout: 10000 }
            );

            if (response.data?.success && Array.isArray(response.data.data)) {
              const zdaniyaForSchool = zdaniyaResponse.data.data?.filter(z => z.OU === school.id) || [];
              const diffs = processDifferences(response.data.data, zdaniyaForSchool);

              const issuesCount = Object.values(diffs).reduce(
                  (sum, section) => sum + section.length, 0
              );
              summary.push({
                id: school.id,
                name: school.name,
                differences: diffs,
                issuesCount
              });
            }
          } catch (err) {
            console.error(`Ошибка обработки школы ${school.id}:`, err);
          }
        }

        setSchoolsSummary(summary);
      } catch (err) {
        setError('Не удалось загрузить данные');
        console.error('Ошибка загрузки:', err);
      } finally {
        setLoading(prev => ({ ...prev, schools: false, allSchools: false }));
      }
    };

    fetchAllData();
  }, []);

  useEffect(() => {
    if (!selectedSchool) return;

    const fetchDifferences = async () => {
      try {
        setLoading(prev => ({ ...prev, selectedSchool: true }));
        setError(null);

        const diffs = await fetchSchoolDifferences(selectedSchool);
        if (diffs) {
          setDifferences(diffs);

          const hasDifferences = Object.values(diffs).some(
              sectionDifferences => sectionDifferences.length > 0
          );

          if (!hasDifferences) {
            setError('Нет различий во всех разделах');
          }
        }
      } catch (err) {
        setError('Ошибка загрузки данных школы');
        console.error('Ошибка загрузки:', err);
      } finally {
        setLoading(prev => ({ ...prev, selectedSchool: false }));
      }
    };

    fetchDifferences();
  }, [selectedSchool]);

  const renderDifferencesTable = (section) => {
    const sectionDifferences = differences[section] || [];

    if (sectionDifferences.length === 0) {
      return null;
    }

    if (section === '1.1') {
      return (
          <div className="difference-message">
            <h2>{sectionTitles[section]}</h2>
            <table className="differences-table text-danger">
              <thead>
                <tr>
                  <th>Строка</th>
                  <th>2025</th>
                  <th>Разница</th>
                  <th>Ожидаемое</th>
                  <th>Комментарий</th>
                  <th>Критичность</th>
                </tr>
              </thead>
              <tbody>
                {sectionDifferences.map((item, index) => (
                  <tr key={`1.1-${index}`}>
                    <td>{item.row || '—'}</td>
                    <td>{item.value_2024 || '—'}</td>
                    <td className={
                      item.difference === 'Несоответствие' ? 'text-danger' :
                      item.difference === 'Разные' ? 'text-warning' : ''
                    }>
                      {item.difference}
                    </td>
                    <td>{item.expected || '—'}</td>
                    <td>{item.message || '—'}</td>
                    <td>{item.severity || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
      );
    }

    if (section === '3.1') {
      const row10Diff = sectionDifferences.find(item => item.row === '10' && item.col === '3');
      const row5Diff = sectionDifferences.find(item => item.row === '5' && item.col === '3');
      const row12Diff = sectionDifferences.find(item => item.row === '12' && item.col === '3');

      return (
          <div className="differences-container">
            <h2>{sectionTitles[section]}</h2>
            {row5Diff && (
                <div className="difference-message text-danger">
                  🟡 Строка 5 колонка 3: значение должно быть 0, фактически {row5Diff.value_2024}
                </div>
            )}
            {row10Diff && (
                <div className="difference-message text-danger">
                  🔴 На конец 2024 года было {row10Diff.value_2023}, на начало 2025 года {row10Diff.value_2024}. (Разница в {(row10Diff.value_2023-row10Diff.value_2024).toFixed(1)})
                </div>
            )}
            {row12Diff && (
                <div className="difference-message text-danger">
                  🔴 Строка 12 колонка 3: значение должно быть 1, фактически {row12Diff.value_2024}
                </div>
            )}
          </div>
      );
    }

    if (section === '2.3') {
      const row1Diff = sectionDifferences.find(item => item.row === '1' && item.col === '3');
      const row2Diff = sectionDifferences.find(item => item.row === '2' && item.col === '3');

      return (
          <div className="differences-container">
            <h2>{sectionTitles[section]}</h2>
            {row1Diff && (
                <div className="difference-message text-danger">
                  🔴 Строка 1 колонка 3: {row1Diff.value_2024} (в ЕМТС - более 50 Мбит/с)
                </div>
            )}
            {row2Diff && (
                <div className="difference-message text-danger">
                  🔴 Строка 2 колонка 3: {row2Diff.value_2024} (в ЕМТС - более 50 Мбит/с)
                </div>
            )}
          </div>
      );
    }

    if (section === '3.2') {
      const row20Diff = sectionDifferences.find(item => item.row === '20' && item.col === '3');

      return (
          <div className="differences-container">
            <h2>{sectionTitles[section]}</h2>
            {row20Diff && (
                <div className="difference-message text-danger">
                  🔴 Строка 20 колонка 3: значение должно быть 1, фактически {row20Diff.value_2024}
                </div>
            )}
          </div>
      );
    }

    if (section === '3.3') {
      const row11Diff = sectionDifferences.find(item => item.row === '11' && item.col === '3');

      return (
          <div className="differences-container">
            <h2>{sectionTitles[section]}</h2>
            {row11Diff && (
                <div className="difference-message text-danger">
                  🔴 Строка 11 колонка 3: значение должно быть 1, фактически {row11Diff.value_2024}
                </div>
            )}
          </div>
      );
    }

    const filteredDifferences = section === '1.1'
        ? sectionDifferences.filter(item => {
          if (item.row === '1' && item.col === '19') {
            return true;
          }
          return item.value_2023 !== item.value_2024;
        })
        : sectionDifferences;

    if (filteredDifferences.length === 0) {
      return null;
    }

    if (section === '2.6' && filteredDifferences[0]?.difference === 'Несоответствие формуле') {
      const { expected, actual, details } = filteredDifferences[0];
      return (
          <div className="differences-container">
            <h2>{sectionTitles[section]}</h2>
            <div className="formula-check differences-container text-danger">
              <div className="formula">
                <span>Формула: </span>
                <span>(Строка 1 графа 5 за 2023) + (Строка 1 графа 3 за 2025) - (Строка 1 графа 4 за 2025) = (Строка 1 графа 5 за 2025)</span>
              </div>
              <div className="values">
                <div className="value-row">
                  <span>Значения:</span>
                  <span>{details.row1Col5_2023 || '0'} + {details.row1Col3_2024 || '0'} - {details.row1Col4_2024 || '0'} ≠ {actual}</span>
                </div>
                <div className="expected-row">
                  <span>Ожидаемый результат:</span>
                  <span>{expected}</span>
                </div>
                <div className="actual-row">
                  <span>Фактический результат:</span>
                  <span>{actual}</span>
                </div>
              </div>
              <div className="result text-danger">
                🔴 Несоответствие формуле!
              </div>
            </div>
          </div>
      );
    }

    if (section === '2.1') {
      const formulaDiff = sectionDifferences.find(item =>
          item.row === '1' && item.col === '3' && item.difference === 'Несоответствие формуле'
      );

      const otherDiffs = sectionDifferences.filter(item =>
          !(item.row === '1' && item.col === '3' && item.difference === 'Несоответствие формуле')
      );

      return (
          <div className="differences-container">
            <h2>{sectionTitles[section]}</h2>

            {formulaDiff && (
                <div className="formula-check differences-container text-danger">
                  <div className="formula">
                    <span>Формула: </span>
                    <span>(Строка 1 графа 3 за 2025)=(Строка 1 графа 3 за 2024) + (Строка 7 графа 3 за 2025)</span>
                  </div>
                  <div className="values">
                    <div className="value-row">
                      <span>Значения:</span>
                      <span>{formulaDiff.details.row1Col3_2023 || '0'} + {formulaDiff.details.row7Col3_2023 || '0'} ≠ {formulaDiff.actual}</span>
                    </div>
                    <div className="expected-row">
                      <span>Ожидаемый результат:</span>
                      <span>{formulaDiff.expected}</span>
                    </div>
                    <div className="actual-row">
                      <span>Фактический результат:</span>
                      <span>{formulaDiff.actual}</span>
                    </div>
                  </div>
                  <div className="result text-danger">
                    🔴 Несоответствие формуле! Было списание?
                  </div>
                </div>
            )}

            {otherDiffs.length > 0 && (
                <>
                  <h3>Различия по позициям с прошлым годом (информация):</h3>
                  <table className="differences-table">
                    <thead>
                      <tr>
                        <th>Строка</th>
                        <th>Колонка</th>
                        <th>2024</th>
                        <th>2025</th>
                        <th>Разница</th>
                        <th>Тип</th>
                      </tr>
                    </thead>
                    <tbody>
                      {otherDiffs.map((item, index) => (
                        <tr key={`${section}-other-${index}`}>
                          <td>{item.row || '—'}</td>
                          <td>{item.col || '—'}</td>
                          <td>{item.value_2023 || '—'}</td>
                          <td>{item.value_2024 || '—'}</td>
                          <td className={
                            item.difference === 'Разные' ? 'text-warning' :
                            item.difference === 'Предупреждение' ? 'text-warning' :
                            item.difference === 'Несоответствие' ? 'text-danger' :
                            'text-danger'
                          }>
                            {item.difference}
                          </td>
                          <td>{item.severity || '🔵'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
            )}
          </div>
      );
    }

    if (section === '1.4') {
      const row1Diff = sectionDifferences.find(item => item.row === '1' && item.col === '3');
      const otherDiffs = sectionDifferences.filter(item => !(item.row === '1' && item.col === '3'));

      return (
          <div className="differences-container">
            <h2>{sectionTitles[section]}</h2>
            {row1Diff && (
                <div className="difference-message text-danger">
                  🔴 Строка 1 колонка 3: значение {row1Diff.value_2024} не соответствует значению из раздела 3.4 (строка 2 колонка 3) - {row1Diff.expected}
                </div>
            )}
            {otherDiffs.length > 0 && (
                <table className="differences-table">
                  <thead>
                    <tr>
                      <th>Строка</th>
                      <th>Колонка</th>
                      <th>2024</th>
                      <th>2025</th>
                      <th>Разница</th>
                      <th>Сообщение</th>
                      <th>Критичность</th>
                    </tr>
                  </thead>
                  <tbody>
                    {otherDiffs.map((item, index) => (
                      <tr key={`${section}-${index}`}>
                        <td>{item.row || '—'}</td>
                        <td>{item.col || '—'}</td>
                        <td>{item.value_2023 || '—'}</td>
                        <td>{item.value_2024 || '—'}</td>
                        <td className={
                          item.difference === 'Разные' ? 'text-warning' :
                          item.difference === 'Предупреждение' ? 'text-warning' :
                          item.difference === 'Несоответствие' ? 'text-danger' :
                          'text-danger'
                        }>
                          {item.difference}
                        </td>
                        <td>{item.message || '—'}</td>
                        <td>{item.severity || '🔵'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            )}
          </div>
      );
    }

    return (
        <div className="differences-container">
          <h2>{sectionTitles[section]}</h2>
          <table className="differences-table">
            <thead>
              <tr>
                <th>Строка</th>
                <th>Колонка</th>
                <th>2024</th>
                <th>2025</th>
                <th>Разница</th>
                {['1.1', '3.1', '3.2', '3.3', '3.6'].includes(section) && <th>Ожидаемое</th>}
                {section === '3.5' && <th>Сообщение</th>}
                <th>Критичность</th>
              </tr>
            </thead>
            <tbody>
              {filteredDifferences.map((item, index) => (
                <tr key={`${section}-${index}`}>
                  <td>{item.row || '—'}</td>
                  <td>{item.col || '—'}</td>
                  <td>{item.value_2023 || '—'}</td>
                  <td>{item.value_2024 || '—'}</td>
                  <td className={
                    item.difference === 'Разные' ? 'text-warning' :
                    item.difference === 'Предупреждение' ? 'text-warning' :
                    item.difference === 'Несоответствие' ? 'text-danger' :
                    'text-danger'
                  }>
                    {item.difference}
                  </td>
                  {['1.1', '3.1', '3.2', '3.3', '3.6'].includes(section) && (
                    <td>{item.expected || '—'}</td>
                  )}
                  {section === '3.5' && (
                    <td>{item.message || '—'}</td>
                  )}
                  <td>{item.severity || '🔵'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
    );
  };

 const renderSchoolsOverview = () => {
    if (loading.allSchools) {
      return (
          <div className="loading">
            <div className="spinner"></div>
            <p>Загрузка данных по школам...</p>
          </div>
      );
    }

    if (allSchoolsList.length === 0) {
      return <p>Нет данных для отображения</p>;
    }

    const sections = Object.keys(sectionTitles);

    const summaryMap = {};
    schoolsSummary.forEach(school => {
      summaryMap[school.id] = school;
    });

    return (
        <div className="overview-container">
          <h2>Обзор замечаний по школам</h2>
          <div>
          </div>
          <button
              className="help-button"
              onClick={() => setIsModalOpen(true)}
              title="Описание алгоритма"
          >
            ?
          </button>

          <div className="schools-overview-table-container">
            <table className="schools-overview-table">
              <thead>
                <tr>
                  <th>Школа</th>
                  {sections.map(section => (
                      <th key={section}>{section}</th>
                  ))}
                  <th>ЭЦП</th>
                  <th>Статус отчета</th>
                  <th>Всего</th>
                </tr>
              </thead>
              <tbody>
                {allSchoolsList.map(school => {
                  const schoolSummary = summaryMap[school.id];
                  const reportInfo = reportStatus[school.id];
                  const hasData = reportInfo?.has_data || false;
                  const signatureInfo = signatureStatus[school.id];
                  const hasSignature = signatureInfo?.signature_data === 'yes';
                  const signatureStatusText = hasSignature ? '✓' : (signatureInfo?.signature_data === 'no' ? '✗' : '?');
                  const signatureTitle = hasSignature 
                      ? 'Подпись есть' 
                      : (signatureInfo?.error_message || (signatureInfo?.signature_data === 'no' ? 'Нет подписи' : 'Статус не определен'));
                  
                  const reportSubmitted = hasData;
                  const reportStatusText = reportSubmitted ? 'Предоставлен' : 'Не предоставлен';
                  const reportStatusClass = reportSubmitted ? 'report-submitted' : 'report-not-submitted';
                  const reportStatusTitle = reportSubmitted 
                      ? 'Отчет предоставлен' 
                      : (reportInfo?.error_message || 'Отчет не предоставлен');
                  
                  return (
                      <tr
                          key={school.id}
                          onClick={() => reportSubmitted && setSelectedSchool(school.id)}
                          className={`school-row ${!reportSubmitted ? 'report-not-submitted-row' : ''}`}
                          style={!reportSubmitted ? { cursor: 'not-allowed', opacity: 0.7 } : {}}
                      >
                        <td style={{ fontWeight: 'bold' }}>{school.name}</td>
                        {sections.map(section => {
                          const count = schoolSummary?.differences[section]?.length || 0;
                          if (!reportSubmitted) {
                            return (
                                <td key={section} className="no-data" title="Отчет не предоставлен">
                                  —
                                </td>
                            );
                          }
                          return (
                              <td
                                  key={section}
                                  className={count > 0 ? 'has-issues' : 'no-issues'}
                                  title={count > 0 ? `${count} замечаний` : 'Нет замечаний'}
                              >
                                {count > 0 ? '⚠️' : '✓'}
                              </td>
                          );
                        })}
                        <td 
                          className={hasSignature ? 'signature-yes' : 'signature-no'} 
                          title={signatureTitle}
                          style={{ textAlign: 'center', fontWeight: 'bold' }}
                        >
                          {signatureStatusText}
                        </td>
                        <td 
                          className={reportStatusClass} 
                          title={reportStatusTitle}
                          style={{ textAlign: 'center', fontWeight: 'bold' }}
                        >
                          {reportStatusText}
                        </td>
                        <td className={!reportSubmitted ? 'no-data' : (schoolSummary?.issuesCount > 0 ? 'has-issues' : 'no-issues')}>
                          {reportSubmitted ? (schoolSummary?.issuesCount || 0) : '—'}
                        </td>
                      </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="legend">
            <div className="legend-item">
              Период обновления данных - каждый час
            </div>
            
          </div>
        </div>
    );
  };

  return (
      <div className="App">
        <header className="App-header">
          <h5>Система верификации отчёта ОО-2 <br/> Дата и время последней синхронизации данных с
            https://cabinet.miccedu.ru: {formatLastSync(lastSync)}</h5>

          <button
              className="help-button"
              onClick={() => setIsModalOpen(true)}
              title="Описание алгоритма"
          >
            ?
          </button>
          <p className="textprd">
            Согласно ст. 13.19 КоАП РФ («Нарушение порядка представления статистической информации»):

            Для должностных лиц – штраф от 10 000 до 20 000 руб.

            Для юридических лиц – штраф от 20 000 до 70 000 руб.
            При повторном нарушении штрафы увеличиваются.
            Кто несёт ответственность?
            Руководитель организации – как лицо, подписавшее отчёт.

            Главный бухгалтер/ответственный за отчётность – если ошибка произошла по его вине.

            Юридическое лицо – штрафы по КоАП и НК РФ.
          </p>
        </header>

        <div className="controls">
          <div className="school-selector">
            <label htmlFor="school-select">Выберите школу:</label>
            <select
                id="school-select"
                value={selectedSchool}
                onChange={(e) => setSelectedSchool(e.target.value)}
                disabled={loading.schools || schools.length === 0}
            >
              <option value="">— Сводный отчёт —</option>
              {schools.map(school => (
                  <option key={school.id} value={school.id}>
                    {school.name}
                  </option>
              ))}
            </select>
          </div>
          {selectedSchool && (
              <button
                  className="export-button"
                  onClick={exportToExcel}
                  disabled={loading.selectedSchool}
              >
                Экспорт в Excel
              </button>
          )}
          {!selectedSchool && allSchoolsList.length > 0 && (
              <button
                  className="export-summary-button"
                  onClick={exportSummaryToExcel}
                  disabled={loading.allSchools}
              >
                Экспорт сводного отчета
              </button>
          )}
        </div>

        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <h2>Описание алгоритма проверки</h2>
          <div className="algorithm-description">
            {algorithmDescription.split('\n').map((paragraph, index) => (
                <p key={index} dangerouslySetInnerHTML={{__html: paragraph}}/>
            ))}
          </div>
          <a href="https://max.ru/join/OVpubZddyaDD0bc4tTqkR35unTpXJbaffAZYO-nj8Oo" target="_blank" rel="noopener noreferrer">
            <button>Если остались вопросы, пожелания, предложения</button>
          </a>
        </Modal>

        {error && (
            <div className="error-message">
              {error}
              <button onClick={() => setError(null)} className="close-error">
                ×
              </button>
            </div>
        )}

        {loading.selectedSchool ? (
            <div className="loading">
              <div className="spinner"></div>
              <p>Загрузка данных школы...</p>
            </div>
        ) : selectedSchool ? (
            <div className="tables-container">
              <div className="notice-box">
                <div className="notice-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
                        fill="#3498db"/>
                  </svg>
                </div>
                <div className="notice-content">
                  <h2>
                    Убедительная просьба внимательно прочитать описание{' '}
                    <span className="notice-link" onClick={() => setIsModalOpen(true)}>
                      алгоритма проверки
                    </span>
                  </h2>
                  <div className="notice-phone-warning">
                    <span className="phone-icon">📵</span>
                    По телефону техническая и методическая помощь не осуществляется
                  </div>
                </div>
              </div>
              {Object.keys(sectionTitles).map(section =>
                  renderDifferencesTable(section)
              )}
            </div>
        ) : (
            renderSchoolsOverview()
        )}
        <div className="App-footer">
          <center><img src={'https://sportrobotics.ru/files/41061/filename/LogoIMC%282%29.png'} width={'50px'}
               className="black" alt="logo"/></center>
          <br/>ИМЦ Кировского района. Лаборатория информационных ресурсов управлением образованием<br></br>
          Разработчик Дубров М.А. Версия программы от 28.03.2026<br></br>
          Дата и время последней синхронизации данных с https://cabinet.miccedu.ru/: {formatLastSync(lastSync)}
        </div>
      </div>
  );
}

export default App;